import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Card,
  CardContent,
  Container,
  Grid,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  TextField,
  InputAdornment,
  Breadcrumbs,
  Link,
  Tabs,
  Tab,
  Stack,
  Chip,
  Checkbox,
} from "@mui/material";
import {
  Search,
  Download,
  ArrowBack,
  PeopleAlt,
  ShowChart,
  AccountBalance,
  WhatsApp,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import * as XLSX from "xlsx";
import adminService from "../../redux/admin/adminService";
import { colors } from "../../constants/colors";

interface HoldingPerf {
  holdingId?: number;
  folioId?: number;
  isin?: string;
  name: string;
  invested: number;
  current: number;
  overallGainLoss: number;
  overallPercent: number;
  oneDayChange: number;
  oneDayChangePercentage: number;
}

interface UserRow {
  userId: number;
  name: string;
  aumUserId: string;
  mobile: string;
  accountType: string;
  userType: number;
  onboardedAt: string;
  bestStock: HoldingPerf | null;
  worstStock: HoldingPerf | null;
  bestMf: HoldingPerf | null;
  worstMf: HoldingPerf | null;
}

interface SummaryData {
  totalUsers: number;
  usersWithStocks: number;
  usersWithMf: number;
}

const formatPct = (value: number | undefined | null) => {
  if (value === undefined || value === null || Number.isNaN(Number(value))) {
    return "—";
  }
  const n = Number(value);
  const sign = n > 0 ? "+" : "";
  return `${sign}${n.toFixed(2)}%`;
};

const buildWhatsAppMessage = (user: UserRow) => {
  const displayName = (user.name || "").trim() || "Investor";
  const lines = [
    `Hi ${displayName},`,
    "",
    "Here's a quick look at your portfolio movers:",
    "",
  ];

  if (user.bestStock) {
    lines.push(
      `Best Stock: ${user.bestStock.name} (${formatPct(user.bestStock.overallPercent)} overall | ${formatPct(user.bestStock.oneDayChangePercentage)} today)`
    );
  }
  if (user.bestMf) {
    lines.push(
      `Best MF: ${user.bestMf.name} (${formatPct(user.bestMf.overallPercent)} overall | ${formatPct(user.bestMf.oneDayChangePercentage)} today)`
    );
  }
  if (!user.bestStock && !user.bestMf) {
    lines.push("No active stock or MF holdings to share right now.");
  }

  lines.push("", "— Team AUM Sampann");
  return lines.join("\n");
};

/** Python / Twilio API expects 10-digit Indian mobile (no +91 / 91). */
const normalizeMobileForWhatsApp = (raw: string) => {
  let digits = String(raw || "").replace(/\D/g, "");
  if (digits.startsWith("91") && digits.length === 12) {
    digits = digits.slice(2);
  }
  if (digits.length > 10) {
    digits = digits.slice(-10);
  }
  return digits.length === 10 ? digits : "";
};

const pctColor = (value: number | undefined | null) => {
  if (value === undefined || value === null || Number.isNaN(Number(value))) {
    return "text.secondary";
  }
  const n = Number(value);
  if (n > 0) return "#08991F";
  if (n < 0) return "#D32F2F";
  return "text.secondary";
};

const HoldingCell: React.FC<{
  holding: HoldingPerf | null;
  emptyLabel?: string;
}> = ({ holding, emptyLabel = "—" }) => {
  if (!holding) {
    return (
      <Typography variant="body2" color="text.secondary">
        {emptyLabel}
      </Typography>
    );
  }

  return (
    <Box>
      <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
        {holding.name}
      </Typography>
      <Typography
        variant="caption"
        sx={{ fontWeight: 700, color: pctColor(holding.overallPercent), display: "block" }}
      >
        Overall {formatPct(holding.overallPercent)}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        1D {formatPct(holding.oneDayChangePercentage)}
      </Typography>
    </Box>
  );
};

const BestWorstHoldings: React.FC = () => {
  const navigate = useNavigate();
  const [dateType, setDateType] = useState<string>("7day");
  const [customStartDate, setCustomStartDate] = useState<string>("");
  const [customEndDate, setCustomEndDate] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [summary, setSummary] = useState<SummaryData>({
    totalUsers: 0,
    usersWithStocks: 0,
    usersWithMf: 0,
  });
  const [usersList, setUsersList] = useState<UserRow[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [tabValue, setTabValue] = useState(0);
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
  const [sendingWhatsApp, setSendingWhatsApp] = useState(false);

  const cacheRef = useRef<Record<string, { summary: SummaryData; usersList: UserRow[] }>>({});

  const getDateRange = useCallback(() => {
    const end = new Date();
    const start = new Date();

    switch (dateType) {
      case "1day":
        start.setHours(0, 0, 0, 0);
        break;
      case "7day":
        start.setDate(end.getDate() - 7);
        start.setHours(0, 0, 0, 0);
        break;
      case "1month":
        start.setMonth(end.getMonth() - 1);
        start.setHours(0, 0, 0, 0);
        break;
      case "1year":
        start.setFullYear(end.getFullYear() - 1);
        start.setHours(0, 0, 0, 0);
        break;
      case "max":
        start.setFullYear(2000, 0, 1);
        start.setHours(0, 0, 0, 0);
        break;
      case "custom":
        if (customStartDate && customEndDate) {
          const customStart = new Date(customStartDate);
          customStart.setHours(0, 0, 0, 0);
          const customEnd = new Date(customEndDate);
          customEnd.setHours(23, 59, 59, 999);
          return { startDate: customStart.getTime(), endDate: customEnd.getTime() };
        }
        return null;
      default:
        start.setDate(end.getDate() - 7);
        start.setHours(0, 0, 0, 0);
    }

    return { startDate: start.getTime(), endDate: end.getTime() };
  }, [dateType, customStartDate, customEndDate]);

  const fetchData = useCallback(
    async (isCustomSubmit = false) => {
      if (dateType === "custom" && !isCustomSubmit) return;

      const range = getDateRange();
      if (!range) return;

      const cacheKey =
        dateType === "custom" ? `custom_${range.startDate}_${range.endDate}` : dateType;

      if (cacheRef.current[cacheKey]) {
        setSummary(cacheRef.current[cacheKey].summary);
        setUsersList(cacheRef.current[cacheKey].usersList);
        setSelectedUserIds([]);
        return;
      }

      setLoading(true);
      try {
        const response = await adminService.getSuperAdminOpsBestWorstHoldings({
          startDate: range.startDate,
          endDate: range.endDate,
        });

        if (response && (response.statusCode === 200 || response.success === 200)) {
          const newSummary = {
            totalUsers: Number(response.data.totalUsers || 0),
            usersWithStocks: Number(response.data.usersWithStocks || 0),
            usersWithMf: Number(response.data.usersWithMf || 0),
          };
          const newUsers: UserRow[] = response.data.users || [];

          setSummary(newSummary);
          setUsersList(newUsers);
          setSelectedUserIds([]);
          cacheRef.current[cacheKey] = { summary: newSummary, usersList: newUsers };
        } else {
          toast.error(response.message || "Failed to fetch holdings data");
        }
      } catch (error: any) {
        console.error("Error fetching best/worst holdings:", error);
        toast.error(error.message || "Failed to load best/worst holdings");
      } finally {
        setLoading(false);
      }
    },
    [getDateRange, dateType]
  );

  useEffect(() => {
    if (dateType !== "custom") {
      fetchData();
    }
  }, [dateType, fetchData]);

  const handleCustomApply = () => {
    if (!customStartDate || !customEndDate) {
      toast.error("Please select both start and end dates");
      return;
    }
    fetchData(true);
  };

  const filteredUsers = usersList.filter(
    (user) =>
      (user.name || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
      (user.aumUserId || "").toLowerCase().includes(searchQuery.toLowerCase())
  );

  const allUsers = filteredUsers;
  const withStocks = filteredUsers.filter((u) => u.bestStock || u.worstStock);
  const withMf = filteredUsers.filter((u) => u.bestMf || u.worstMf);

  const displayedUsers =
    tabValue === 0 ? allUsers : tabValue === 1 ? withStocks : withMf;

  const allDisplayedSelected =
    displayedUsers.length > 0 &&
    displayedUsers.every((u) => selectedUserIds.includes(Number(u.userId)));

  const toggleSelectAllDisplayed = () => {
    if (allDisplayedSelected) {
      const displayedIds = new Set(displayedUsers.map((u) => Number(u.userId)));
      setSelectedUserIds((prev) => prev.filter((id) => !displayedIds.has(Number(id))));
      return;
    }
    setSelectedUserIds((prev) => {
      const next = new Set(prev.map((id) => Number(id)));
      displayedUsers.forEach((u) => next.add(Number(u.userId)));
      return Array.from(next);
    });
  };

  const toggleUserSelection = (userId: number) => {
    const id = Number(userId);
    setSelectedUserIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleSendWhatsApp = async () => {
    if (selectedUserIds.length === 0) {
      toast.error("Select at least one user");
      return;
    }

    const selectedIdSet = new Set(selectedUserIds.map((id) => Number(id)));
    const selectedUsers = usersList.filter((u) => selectedIdSet.has(Number(u.userId)));

    const skipped: string[] = [];
    const payload = selectedUsers
      .map((user) => {
        const mobile = normalizeMobileForWhatsApp(user.mobile);
        if (!mobile) {
          skipped.push(`${user.name || user.aumUserId || user.userId} (invalid/missing mobile)`);
          return null;
        }
        return {
          mobile,
          message: buildWhatsAppMessage(user),
        };
      })
      .filter((item): item is { mobile: string; message: string } => item !== null);

    if (payload.length === 0) {
      toast.error(
        skipped.length > 0
          ? `No messages to send. ${skipped.length} selected user(s) skipped (mobile issue).`
          : "No selected users found in the current list"
      );
      return;
    }

    console.log(
      `WhatsApp payload: selected=${selectedUserIds.length}, sending=${payload.length}, skipped=${skipped.length}`,
      payload
    );

    setSendingWhatsApp(true);
    try {
      await adminService.sendWhatsAppMessages(payload);
      if (skipped.length > 0) {
        toast.success(
          `Sent for ${payload.length} of ${selectedUserIds.length} selected. Skipped ${skipped.length} (no valid mobile).`
        );
      } else {
        toast.success(`WhatsApp request sent for ${payload.length} user(s).`);
      }
    } catch (error: any) {
      console.error("Error sending WhatsApp:", error);
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to send WhatsApp messages"
      );
    } finally {
      setSendingWhatsApp(false);
    }
  };

  const handleExport = () => {
    if (displayedUsers.length === 0) {
      toast.error("No data to export");
      return;
    }

    const exportData = displayedUsers.map((user) => ({
      Name: user.name,
      "AUM User ID": user.aumUserId,
      "Account Type": user.accountType,
      Onboarded: user.onboardedAt,
      "Best Stock": user.bestStock?.name || "",
      "Best Stock Overall %": user.bestStock?.overallPercent ?? "",
      "Best Stock 1D %": user.bestStock?.oneDayChangePercentage ?? "",
      "Worst Stock": user.worstStock?.name || "",
      "Worst Stock Overall %": user.worstStock?.overallPercent ?? "",
      "Worst Stock 1D %": user.worstStock?.oneDayChangePercentage ?? "",
      "Best MF": user.bestMf?.name || "",
      "Best MF Overall %": user.bestMf?.overallPercent ?? "",
      "Best MF 1D %": user.bestMf?.oneDayChangePercentage ?? "",
      "Worst MF": user.worstMf?.name || "",
      "Worst MF Overall %": user.worstMf?.overallPercent ?? "",
      "Worst MF 1D %": user.worstMf?.oneDayChangePercentage ?? "",
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Best Worst Holdings");
    XLSX.writeFile(workbook, `Best_Worst_Holdings_${dateType}_${Date.now()}.xlsx`);
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
          <Link
            underline="hover"
            color="inherit"
            href="#"
            onClick={(e) => {
              e.preventDefault();
              navigate("/super-admin/ops-dashboard");
            }}
            sx={{ display: "flex", alignItems: "center" }}
          >
            <ArrowBack sx={{ mr: 0.5, fontSize: "inherit" }} />
            Ops Dashboard
          </Link>
          <Typography color="text.primary">Best / Worst Holdings</Typography>
        </Breadcrumbs>

        <Typography variant="h5" sx={{ fontWeight: 700, color: colors.primary }}>
          Best / Worst Holdings
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Best and worst stock &amp; MF by overall % since invested, for investors
          onboarded in the selected period.
        </Typography>
      </Box>

      <Card sx={{ mb: 4, borderRadius: "12px", border: "1px solid #e8e8e8", boxShadow: "none" }}>
        <CardContent>
          <Grid container spacing={3} alignItems="center" justifyContent="space-between">
            <Grid item xs={12} md={7}>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 600, mb: 1.5, color: "text.secondary" }}
              >
                Select Onboarding Range
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {[
                  { value: "1day", label: "1 Day" },
                  { value: "7day", label: "7 Days" },
                  { value: "1month", label: "1 Month" },
                  { value: "1year", label: "1 Year" },
                  { value: "max", label: "Max" },
                  { value: "custom", label: "Custom Range" },
                ].map((type) => (
                  <Button
                    key={type.value}
                    variant={dateType === type.value ? "contained" : "outlined"}
                    onClick={() => setDateType(type.value)}
                    sx={{
                      borderRadius: "20px",
                      textTransform: "none",
                      px: 3,
                      fontWeight: 600,
                    }}
                  >
                    {type.label}
                  </Button>
                ))}
              </Stack>
            </Grid>

            {dateType === "custom" && (
              <Grid item xs={12} md={5}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, mb: 1, color: "text.secondary" }}
                >
                  Custom Range Dates
                </Typography>
                <Stack direction="row" spacing={2}>
                  <TextField
                    type="date"
                    label="Start Date"
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                    size="small"
                  />
                  <TextField
                    type="date"
                    label="End Date"
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                    size="small"
                  />
                  <Button
                    variant="contained"
                    onClick={handleCustomApply}
                    sx={{ fontWeight: 600, minWidth: "90px" }}
                    disabled={loading || !customStartDate || !customEndDate}
                  >
                    Apply
                  </Button>
                </Stack>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={4}>
          <Card
            sx={{
              border: "1px solid #e8e8e8",
              boxShadow: "none",
              borderRadius: "12px",
              height: "100%",
            }}
          >
            <CardContent
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                height: "100%",
              }}
            >
              <Box>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  sx={{ fontWeight: 600, fontSize: "0.8rem" }}
                >
                  Total Users
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, mt: 1, color: colors.primary }}>
                  {loading ? <CircularProgress size={24} /> : summary.totalUsers}
                </Typography>
              </Box>
              <PeopleAlt sx={{ fontSize: 32, color: colors.primary, opacity: 0.8 }} />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Card
            sx={{
              border: "1px solid #e8e8e8",
              boxShadow: "none",
              borderRadius: "12px",
              height: "100%",
            }}
          >
            <CardContent
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                height: "100%",
              }}
            >
              <Box>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  sx={{ fontWeight: 600, fontSize: "0.8rem" }}
                >
                  With Stocks
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, mt: 1, color: "#0891B2" }}>
                  {loading ? <CircularProgress size={24} /> : summary.usersWithStocks}
                </Typography>
              </Box>
              <ShowChart sx={{ fontSize: 32, color: "#0891B2", opacity: 0.8 }} />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Card
            sx={{
              border: "1px solid #e8e8e8",
              boxShadow: "none",
              borderRadius: "12px",
              height: "100%",
            }}
          >
            <CardContent
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                height: "100%",
              }}
            >
              <Box>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  sx={{ fontWeight: 600, fontSize: "0.8rem" }}
                >
                  With MF
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, mt: 1, color: "#08991F" }}>
                  {loading ? <CircularProgress size={24} /> : summary.usersWithMf}
                </Typography>
              </Box>
              <AccountBalance sx={{ fontSize: 32, color: "#08991F", opacity: 0.8 }} />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper
        elevation={3}
        sx={{
          borderRadius: "12px",
          overflow: "hidden",
          border: "1px solid #e8e8e8",
          boxShadow: "none",
        }}
      >
        <Box
          sx={{
            p: 3,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <TextField
            placeholder="Search by Name or AUM User ID..."
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ width: { xs: "100%", sm: "300px" } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
          <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
            <Button
              variant="outlined"
              startIcon={<WhatsApp />}
              onClick={handleSendWhatsApp}
              disabled={loading || sendingWhatsApp || selectedUserIds.length === 0}
              sx={{ fontWeight: 600 }}
            >
              {sendingWhatsApp
                ? "Sending..."
                : `Send WhatsApp (${selectedUserIds.length})`}
            </Button>
            <Button
              variant="contained"
              startIcon={<Download />}
              onClick={handleExport}
              disabled={loading || displayedUsers.length === 0}
              sx={{ fontWeight: 600 }}
            >
              Export Excel
            </Button>
          </Stack>
        </Box>

        <Box sx={{ borderBottom: 1, borderColor: "divider", px: 3 }}>
          <Tabs
            value={tabValue}
            onChange={(_e, newValue) => setTabValue(newValue)}
            aria-label="holdings filter tabs"
          >
            <Tab label={`All (${allUsers.length})`} sx={{ fontWeight: 600 }} />
            <Tab label={`With Stocks (${withStocks.length})`} sx={{ fontWeight: 600 }} />
            <Tab label={`With MF (${withMf.length})`} sx={{ fontWeight: 600 }} />
          </Tabs>
        </Box>

        <TableContainer sx={{ maxHeight: "550px" }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" sx={{ backgroundColor: colors.primary }}>
                  <Checkbox
                    checked={allDisplayedSelected}
                    indeterminate={
                      selectedUserIds.length > 0 &&
                      !allDisplayedSelected &&
                      displayedUsers.some((u) =>
                        selectedUserIds.includes(Number(u.userId))
                      )
                    }
                    onChange={toggleSelectAllDisplayed}
                    sx={{
                      color: "white",
                      "&.Mui-checked": { color: "white" },
                      "&.MuiCheckbox-indeterminate": { color: "white" },
                    }}
                    disabled={loading || displayedUsers.length === 0}
                  />
                </TableCell>
                <TableCell
                  sx={{ fontWeight: 700, fontSize: 14, color: "white", backgroundColor: colors.primary }}
                >
                  Investor
                </TableCell>
                <TableCell
                  sx={{ fontWeight: 700, fontSize: 14, color: "white", backgroundColor: colors.primary }}
                >
                  Onboarded
                </TableCell>
                <TableCell
                  sx={{ fontWeight: 700, fontSize: 14, color: "white", backgroundColor: colors.primary }}
                >
                  Best Stock
                </TableCell>
                <TableCell
                  sx={{ fontWeight: 700, fontSize: 14, color: "white", backgroundColor: colors.primary }}
                >
                  Worst Stock
                </TableCell>
                <TableCell
                  sx={{ fontWeight: 700, fontSize: 14, color: "white", backgroundColor: colors.primary }}
                >
                  Best MF
                </TableCell>
                <TableCell
                  sx={{ fontWeight: 700, fontSize: 14, color: "white", backgroundColor: colors.primary }}
                >
                  Worst MF
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                    <CircularProgress />
                    <Typography mt={2} color="textSecondary">
                      Fetching best / worst holdings...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : displayedUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                    <Typography color="textSecondary" fontSize={15}>
                      No investors found for the selected onboarding period.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                displayedUsers.map((user) => (
                  <TableRow
                    key={user.userId}
                    hover
                    selected={selectedUserIds.includes(Number(user.userId))}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedUserIds.includes(Number(user.userId))}
                        onChange={() => toggleUserSelection(Number(user.userId))}
                      />
                    </TableCell>
                    <TableCell sx={{ py: 2, minWidth: 160 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {user.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {user.aumUserId}
                      </Typography>
                      <Chip
                        label={user.accountType}
                        size="small"
                        sx={{
                          mt: 0.5,
                          fontWeight: 600,
                          bgcolor:
                            user.accountType === "Individual" ? "success.light" : "info.light",
                          color:
                            user.accountType === "Individual" ? "success.dark" : "info.dark",
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>{user.onboardedAt || "—"}</TableCell>
                    <TableCell sx={{ py: 2, minWidth: 150 }}>
                      <HoldingCell holding={user.bestStock} />
                    </TableCell>
                    <TableCell sx={{ py: 2, minWidth: 150 }}>
                      <HoldingCell holding={user.worstStock} />
                    </TableCell>
                    <TableCell sx={{ py: 2, minWidth: 180 }}>
                      <HoldingCell holding={user.bestMf} />
                    </TableCell>
                    <TableCell sx={{ py: 2, minWidth: 180 }}>
                      <HoldingCell holding={user.worstMf} />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Container>
  );
};

export default BestWorstHoldings;
